import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { MoreHorizontal, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import Button from '@/components/primitives/Button.jsx';
import Menu from '@/components/patterns/Menu.jsx';
import ExecutionStatus from '@/components/surfaces/ExecutionStatus.jsx';
import toastManager from '@/lib/toast/toastManager.js';
import checkConnection from '@/redux/actions/pieveActions/checkConnection.js';
import resolveConnectionState from '../lib/resolveConnectionState.js';
import AppLogo from '@/components/surfaces/AppLogo.jsx';
import RemoveConnectionDialog from './RemoveConnectionDialog.jsx';
import RenameConnectionDialog from './RenameConnectionDialog.jsx';

/** One saved connection: which app and account, whether it works, and what you can do with it. */
const ConnectionRow = ({ connection, appVersion, showApp = true }) => {
  const dispatch = useDispatch();
  const [isChecking, setIsChecking] = useState(false);
  const [dialog, setDialog] = useState(null);
  const status = resolveConnectionState({ connection, isChecking });
  const label = connection?.meta?.label || connection?.appName;

  const handleCheck = async () => {
    setIsChecking(true);
    const res = await dispatch(checkConnection({ connection, appVersion }));
    setIsChecking(false);

    if (res?.success === false) {
      toastManager.add({ type: 'error', title: `${connection.appName} did not accept the connection`, description: res.message });
      return;
    }

    toastManager.add({ type: 'success', title: `${label} works` });
  };

  return (
    <li className={showApp ? 'connections-row' : 'connections-row connections-row--plain'}>
      {showApp && <AppLogo app={connection} />}
      <div className="connections-row__text">
        {showApp ? (
          <Link className="connections-row__app" to={`/connections/${encodeURIComponent(connection.appSlug)}`}>
            {connection.appName}
          </Link>
        ) : (
          <span className="connections-row__app">{label}</span>
        )}
        <span className="connections-row__label">{showApp ? label : `Added by ${connection.ownerName || 'you'}`}</span>
      </div>
      <ExecutionStatus state={status.state} label={status.label} size="sm" />
      <div className="connections-row__actions">
        <Button size="sm" variant="secondary" iconStart={RefreshCw} onClick={handleCheck} disabled={isChecking || !appVersion}>
          Check
        </Button>
        <Menu align="end" trigger={<Button size="sm" variant="ghost" iconOnly iconStart={MoreHorizontal} label={`More for ${label}`} />}>
          <Menu.Item icon={Pencil} onClick={() => setDialog('rename')}>
            Rename
          </Menu.Item>
          <Menu.Item icon={Trash2} onClick={() => setDialog('remove')}>
            Remove
          </Menu.Item>
        </Menu>
      </div>
      <RenameConnectionDialog key={label} connection={connection} open={dialog === 'rename'} onOpenChange={(open) => setDialog(open ? 'rename' : null)} />
      <RemoveConnectionDialog connection={connection} open={dialog === 'remove'} onOpenChange={(open) => setDialog(open ? 'remove' : null)} />
    </li>
  );
};

export default ConnectionRow;
